import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { environment } from '../../environments/environment';
import {
  getHostPreLoginBootstrap,
  HOST_PRE_LOGIN_BOOTSTRAP,
  HostPreLoginBootstrapEntry,
} from '../shared/constants/host-pre-login.config';
import {
  DEFAULT_MENU_VERSION,
  MenuVersionEnum,
  parseMenuVersion,
} from '../shared/sidebar/constants/menu-version.enum';
import { mainFeatures } from '../shared/sidebar/menu-items';

/** Configuración de metadata para white label (usada internamente). */
export interface WhiteLabelMetadataConfig {
  type: string;
  title: string;
  faviconUrl: string;
  loginLogoUrl: string;
}

/** Estructura de FileUpload del backend. */
interface FileUpload {
  url?: string;
  filename?: string;
  type?: string;
  size?: number;
  id?: string;
  nameServer?: string;
}

/** Estructura de white label dashboard del backend (projects.dashboard). */
export interface WhiteLabelDashboardConfig {
  title?: string;
  loginPath?: string;
  initialRoute?: string;
  styles?: {
    images?: {
      faviconUrl?: FileUpload;
      loginLogoUrl?: FileUpload;
    };
  };
  menu?: {
    /** {@link MenuVersionEnum}: menú legacy plano vs agrupado por secciones. */
    version?: MenuVersionEnum;
    showFeature?: string[];
    styles?: {
      images?: {
        logoRail?: FileUpload;
        logoFull?: FileUpload;
      };
    };
  };
}

const DEFAULT_CONFIG: WhiteLabelMetadataConfig = {
  type: 'munily',
  title: 'Munily | Dashboard',
  faviconUrl: './favicon.ico',
  loginLogoUrl: 'https://munily-public-cdn.s3.amazonaws.com/Logos/dashboard/munily_logo_medium.png',
};

/**
 * Slugs de rutas de login permitidas. Cada slug se corresponde a
 * `/authentication/<slug>/login` (excepto `default` que es la ruta principal
 * de Munily: `/authentication/login`). Agregar aqui un nuevo slug es la
 * forma de documentar/habilitar otra ruta white-label.
 */
export const ALLOWED_LOGIN_ROUTES = ['default', 'mts'] as const;
export type AllowedLoginRoute = (typeof ALLOWED_LOGIN_ROUTES)[number];

export type PreLoginRouteSlug = Exclude<AllowedLoginRoute, 'default'>;

/** Ruta Angular para login white-label por slug (sin prefijo de hash). */
export function authenticationLoginPathForSlug(slug: PreLoginRouteSlug): string {
  return `/authentication/${slug}/login`;
}

/** Detecta si `routerUrl` corresponde a algún slug en `ALLOWED_LOGIN_ROUTES` (excepto default). */
export function matchPreLoginSlugFromRouterUrl(routerUrl: string): PreLoginRouteSlug | null {
  for (const slug of ALLOWED_LOGIN_ROUTES) {
    if (slug === 'default') continue;
    if (routerUrl.includes(authenticationLoginPathForSlug(slug))) return slug;
  }
  return null;
}

/** Dominio → slug (derivado de HOST_PRE_LOGIN_BOOTSTRAP). */
export const HOST_PRE_LOGIN_SLUG: Record<string, PreLoginRouteSlug> = Object.entries(
  HOST_PRE_LOGIN_BOOTSTRAP,
).reduce<Record<string, PreLoginRouteSlug>>((acc, [host, entry]) => {
  if (isPreLoginRouteSlug(entry.slug)) {
    acc[host] = entry.slug;
  }
  return acc;
}, {});

const PRE_LOGIN_WHITE_LABEL_CONFIG: Record<string, WhiteLabelMetadataConfig> = Object.values(
  HOST_PRE_LOGIN_BOOTSTRAP,
).reduce<Record<string, WhiteLabelMetadataConfig>>((acc, entry) => {
  acc[entry.slug] = bootstrapEntryToMetadata(entry);
  return acc;
}, {});

function isPreLoginRouteSlug(slug: string): slug is PreLoginRouteSlug {
  return slug !== 'default' && (ALLOWED_LOGIN_ROUTES as readonly string[]).includes(slug);
}

function bootstrapEntryToMetadata(entry: HostPreLoginBootstrapEntry): WhiteLabelMetadataConfig {
  return {
    type: entry.slug,
    title: entry.title,
    faviconUrl: entry.faviconUrl,
    loginLogoUrl: entry.loginLogoUrl,
  };
}

const DEFAULT_LOGIN_PATH = '/authentication/login';

/** Resuelve slug pre-login por hostname (función pura, testeable). */
export function resolvePreLoginSlugForHostname(
  hostname: string,
  envBootstrap: Record<string, HostPreLoginBootstrapEntry> = environment.hostPreLoginBootstrap ?? {},
  envSlugHosts: Record<string, PreLoginRouteSlug> = environment.whiteLabelHosts ?? {},
): PreLoginRouteSlug | null {
  const bootstrap = getHostPreLoginBootstrap(hostname, envBootstrap);
  if (bootstrap?.slug && isPreLoginRouteSlug(bootstrap.slug)) {
    return bootstrap.slug;
  }
  const slug = { ...HOST_PRE_LOGIN_SLUG, ...envSlugHosts }[hostname.toLowerCase()];
  return slug ?? null;
}

@Injectable()
export class WhiteLabelService {
  private currentConfig: WhiteLabelMetadataConfig = { ...DEFAULT_CONFIG };
  private sessionLoginPath: string | null = null;

  constructor(private titleService: Title) {}

  setSessionLoginPathFromBuilding(building: any): void {
    this.sessionLoginPath = this.getLoginPathForBuilding(building);
  }

  /**
   * Ruta de login tras logout o sin sesión.
   * Prioridad: hostname (dominio dedicado) → loginPath del edificio → Munily default.
   */
  getLoginRedirectUrl(hostname?: string): string {
    const hostSlug = this.getPreLoginSlugForHost(hostname);
    if (hostSlug) {
      return authenticationLoginPathForSlug(hostSlug);
    }
    if (this.sessionLoginPath) {
      return this.sessionLoginPath;
    }
    return DEFAULT_LOGIN_PATH;
  }

  getLoginPath(): string {
    return this.getLoginRedirectUrl();
  }

  getPreLoginSlugForHost(hostname?: string): PreLoginRouteSlug | null {
    const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
    if (!host) {
      return null;
    }
    return resolvePreLoginSlugForHostname(host);
  }

  /** Aplica branding pre-login del hostname actual; devuelve el slug si aplica. */
  applyPreLoginForCurrentHost(hostname?: string): PreLoginRouteSlug | null {
    const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
    if (!host) {
      return null;
    }
    const bootstrap = getHostPreLoginBootstrap(host, environment.hostPreLoginBootstrap ?? {});
    if (bootstrap) {
      this.applyConfig(bootstrapEntryToMetadata(bootstrap));
      return isPreLoginRouteSlug(bootstrap.slug) ? bootstrap.slug : null;
    }
    const slug = this.getPreLoginSlugForHost(host);
    if (slug) {
      this.applyByRoute(slug);
    }
    return slug;
  }

  clearSessionLoginPath(): void {
    this.sessionLoginPath = null;
  }

  /** Calcula la ruta de login según la config white-label del edificio (función pura). */
  getLoginPathForBuilding(building: any): string {
    const loginPath = this.getDashboardConfig(building)?.loginPath;
    if (loginPath && typeof loginPath === 'string' && loginPath.trim()) {
      const path = loginPath.trim();
      return path.startsWith('/') ? path : `/authentication/${path}/login`;
    }
    return DEFAULT_LOGIN_PATH;
  }

  /** Aplica config hardcodeada por slug (pre-login, sin building disponible). */
  applyByRoute(routeSlug: string): void {
    const config = PRE_LOGIN_WHITE_LABEL_CONFIG[routeSlug?.toLowerCase()];
    if (config) {
      this.applyConfig(config);
    }
  }

  /** Aplica config desde el building (post-login, usa projects.dashboard). */
  applyFromBuilding(building: any): void {
    const dashboard = this.getDashboardConfig(building);

    // Si no hay dashboard config, resetear a default
    if (!dashboard) {
      this.resetToDefault();
      return;
    }

    // Solo aplicar si hay al menos un valor configurado
    const hasCustomConfig = dashboard.title || dashboard.styles?.images?.faviconUrl?.url || dashboard.styles?.images?.loginLogoUrl?.url;

    // Si no hay config personalizada, resetear a default
    if (!hasCustomConfig) {
      this.resetToDefault();
      return;
    }

    this.applyConfig({
      type: dashboard.loginPath || 'custom',
      title: dashboard.title || DEFAULT_CONFIG.title,
      faviconUrl: dashboard.styles?.images?.faviconUrl?.url || DEFAULT_CONFIG.faviconUrl,
      loginLogoUrl: dashboard.styles?.images?.loginLogoUrl?.url || DEFAULT_CONFIG.loginLogoUrl,
    });
  }

  resetToDefault(): void {
    this.applyConfig(DEFAULT_CONFIG);
  }

  getCurrentConfig(): WhiteLabelMetadataConfig {
    return { ...this.currentConfig };
  }

  isWhiteLabel(): boolean {
    return this.currentConfig.type !== DEFAULT_CONFIG.type;
  }

  /** Obtiene la config de dashboard desde el building (projects.dashboard). */
  getDashboardConfig(building: any): WhiteLabelDashboardConfig | undefined {
    return building?.config?.whiteLabel?.projects?.dashboard;
  }

  /** E.164 para burbuja WhatsApp en sesion: `config.whiteLabel.ecosystem.support`. */
  getBuildingWhatsappE164(building: any): string | null {
    const raw =
      building?.config?.whiteLabel?.ecosystem?.support?.phone?.e164Number;
    if (raw && typeof raw === 'string' && raw.trim()) {
      return raw.trim();
    }
    return null;
  }

  /** Obtiene la URL del logo rail desde la config del building. */
  getMenuLogoRailUrl(building: any): string | undefined {
    return this.getDashboardConfig(building)?.menu?.styles?.images?.logoRail?.url;
  }

  /** Obtiene la URL del logo full desde la config del building. */
  getMenuLogoFullUrl(building: any): string | undefined {
    return this.getDashboardConfig(building)?.menu?.styles?.images?.logoFull?.url;
  }

  /** Obtiene los features a mostrar en el menú desde la config del building. */
  getMenuShowFeatures(building: any): string[] | undefined {
    return this.getDashboardConfig(building)?.menu?.showFeature;
  }

  /**
   * Versión del menú lateral para el PH.
   * `whiteLabel.projects.dashboard.menu.version`: {@link MenuVersionEnum}.
   * Sin config → {@link DEFAULT_MENU_VERSION}.
   */
  getMenuVersion(building: any): MenuVersionEnum {
    return parseMenuVersion(this.getDashboardConfig(building)?.menu?.version);
  }

  /** Indica si el menú white label del edificio incluye un feature concreto. */
  hasMenuFeature(building: any, feature: mainFeatures): boolean {
    const showFeatures = this.getMenuShowFeatures(building);
    return Array.isArray(showFeatures) && showFeatures.includes(feature);
  }

  /** Oculta visitante, paquetería y delivery en el filtro de tipo de registro (PH con gestión de visitas business). */
  shouldHideBasicRegistrationTypes(building: any): boolean {
    return this.hasMenuFeature(building, mainFeatures.managementVisitsMacroBusiness);
  }

  /** Obtiene la ruta inicial desde la config del building. */
  getInitialRoute(building: any): string | undefined {
    return this.getDashboardConfig(building)?.initialRoute;
  }

  private applyConfig(config: WhiteLabelMetadataConfig): void {
    this.currentConfig = { ...config };
    this.titleService.setTitle(config.title);
    this.setFavicon(config.faviconUrl);
  }

  private setFavicon(url: string): void {
    let link: HTMLLinkElement = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'shortcut icon';
      document.head.appendChild(link);
    }
    link.type = 'image/x-icon';
    link.href = url;
  }
}
